import {createEffect, mergeProps, on, splitProps} from 'solid-js';

import {i18n, LangPackKey} from '@lib/langPack';
import {InstanceOf} from '@types';

import InputField, {InputFieldOptions, InputFieldValue, InputState} from '@components/inputField';

export interface InputFieldTsxProps<T extends typeof InputField> extends InputFieldOptions {
  InputFieldClass?: T

  instanceRef?: (value: InstanceOf<T>) => void

  class?: string
  value?: InputFieldValue
  onRawInput?: (value: string) => void
  errorLabel?: LangPackKey | null
  errorLabelOptions?: any[]
  errorDescriptionId?: string
  disabled?: boolean
}

export const InputFieldTsx = <T extends typeof InputField>(inProps: InputFieldTsxProps<T>) => {
  const props = mergeProps({InputFieldClass: InputField}, inProps);

  const [, options] = splitProps(
    props,
    ['class', 'value', 'InputFieldClass', 'errorLabel', 'errorLabelOptions', 'errorDescriptionId', 'disabled']
  )

  const obj = new props.InputFieldClass(options)
  props.instanceRef?.(obj as InstanceOf<T>)

  createEffect(on(
    () => props.class,
    (value, prev) => {
      prev && obj.container.classList.remove(prev)
      value && obj.container.classList.add(value)
    }
  ))

  createEffect(on(
    () => [props.errorLabel, props.errorLabelOptions] as const,
    ([error, options], prev) => {
      if(!error && !prev) return // Prevent setting error first render

      const isError = error !== undefined
      if(isError) obj.setError(error, options)
      else obj.setState(InputState.Neutral)
    }
  ))

  createEffect(on(
    () => props.errorDescriptionId,
    (value, prev) => {
      const describedBy = new Set(
        (obj.input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
      );
      if(prev) describedBy.delete(prev);
      if(value) describedBy.add(value);

      if(describedBy.size) {
        obj.input.setAttribute('aria-describedby', [...describedBy].join(' '));
      } else {
        obj.input.removeAttribute('aria-describedby');
      }
    }
  ))

  createEffect(on(
    () => props.value,
    (value) => {
      if(value !== obj.value && value !== undefined) {
        obj.value = value
      }
    }
  ))

  createEffect(on(
    () => [props.label, props.labelOptions] as const,
    ([value, options]) => {
      if(value !== obj.label?.textContent) {
        obj.label.replaceChildren(i18n(value, options))
      }
    }
  ))

  createEffect(on(
    () => props.disabled,
    (value) => {
      obj.input.toggleAttribute('disabled', !!value)
    }
  ))

  return obj.container
}
